<template>
    <section class="vp-end" noautohide :aria-label="endLabel">
        <div class="vp-end-header">
            <span class="vp-label">{{ endLabel }}</span>
            <span class="vp-spacer"></span>
            <button type="button" class="vp-outline-button" @click="emit('replay')">
                <VideoIcon name="replay" :size="18" />
                {{ t("Watch again") }}
            </button>
        </div>

        <div class="vp-end-body">
            <div class="vp-end-main">
                <template v-if="nextEpisode">
                    <span class="vp-label vp-end-next-label">{{ t("Next episode") }}</span>

                    <a :href="nextEpisode.url" class="vp-end-next">
                        <img v-if="nextEpisode.artwork" :src="nextEpisode.artwork" alt="" class="vp-end-next-image" />
                        <span class="vp-end-next-text">
                            <span class="vp-heading vp-end-next-title">{{ nextTitle }}</span>
                            <span class="vp-mono vp-end-next-duration">{{ formatClock(nextEpisode.duration) }}</span>
                        </span>
                    </a>

                    <div v-if="countdownActive" class="vp-end-countdown">
                        <div class="vp-end-countdown-track">
                            <div class="vp-end-countdown-fill" :style="{ width: `${progress}%` }"></div>
                        </div>
                        <span class="vp-mono vp-end-countdown-text">{{ t("Starts in {s} s", { s: remaining }) }}</span>
                        <button type="button" class="vp-outline-button vp-outline-button--small" @click="cancel">{{ t("Cancel") }}</button>
                    </div>
                </template>

                <div class="vp-end-links">
                    <a :href="allEpisodesUrl" class="vp-end-link">{{ t("All episodes") }}</a>
                    <template v-if="followLinks.length > 0">
                        <span class="vp-dot">·</span>
                        <button type="button" class="vp-end-link" :aria-expanded="followOpen" @click="followOpen = !followOpen">
                            {{ t("Follow in your podcast app") }}
                        </button>
                    </template>
                </div>

                <div v-if="followOpen" class="vp-end-follow">
                    <a
                        v-for="link in followLinks"
                        :key="link.service"
                        :href="link.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="vp-outline-button vp-outline-button--small"
                    >
                        {{ link.label }}
                    </a>
                </div>
            </div>

            <aside v-if="podcast.branding" class="vp-promo">
                <div class="vp-promo-brand">
                    <SpringcastMark :size="30" :frame-opacity="0.22" />
                    <span class="vp-label vp-promo-label">Springcast</span>
                </div>
                <h2 class="vp-heading vp-promo-title">{{ t("Get a player like this for your podcast") }}</h2>
                <p class="vp-promo-body">{{ t("Hosting, video, transcripts and stats in one place — European infrastructure, no plug-ins needed.") }}</p>
                <a href="https://www.springcast.io" target="_blank" rel="noopener noreferrer" class="vp-promo-button">
                    {{ t("Book a call") }}
                    <VideoIcon name="arrow" :size="18" />
                </a>
                <div class="vp-promo-trust">
                    <VideoIcon name="shield" :size="16" class="vp-promo-shield" />
                    {{ t("ISO 27001 · GDPR") }}
                </div>
            </aside>
        </div>
    </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import SpringcastMark from "../../PlayerCore/Components/SpringcastMark.vue"
import VideoIcon from "../../PlayerCore/Components/VideoIcon.vue"
import { formatClock } from "../../PlayerCore/format.js"
import { useCoreI18n } from "../../PlayerCore/useCoreI18n.js"

const COUNTDOWN_SECONDS = 4

const LINK_LABELS = {
    apple: "Apple Podcasts",
    spotify: "Spotify",
    youtube: "YouTube",
    google: "Google Podcasts",
}

const props = defineProps({
    episode: { type: Object, required: true },
    podcast: { type: Object, required: true },
    nextEpisode: { type: Object, default: null },
    allEpisodesUrl: { type: String, required: true },
})

const emit = defineEmits(["replay"])

const t = useCoreI18n()
const remaining = ref(COUNTDOWN_SECONDS)
const countdownActive = ref(props.nextEpisode !== null)
const followOpen = ref(false)
let timer = null

const endLabel = computed(() => (props.episode.episode > 0 ? t("End of episode {n}", { n: props.episode.episode }) : t("End of episode")))
const nextTitle = computed(() =>
    props.nextEpisode.episode > 0 ? `${t("Ep. {n}", { n: props.nextEpisode.episode })} — ${props.nextEpisode.title}` : props.nextEpisode.title,
)
const progress = computed(() => ((COUNTDOWN_SECONDS - remaining.value) / COUNTDOWN_SECONDS) * 100)
const followLinks = computed(() =>
    Object.entries(LINK_LABELS)
        .map(([service, label]) => ({ service, label, url: props.podcast.links?.[service] ?? null }))
        .filter((link) => typeof link.url === "string" && /^https?:\/\//i.test(link.url)),
)

function stop() {
    if (timer) {
        clearInterval(timer)
        timer = null
    }
}

function cancel() {
    countdownActive.value = false
    stop()
}

function tick() {
    if (!countdownActive.value) {
        stop()

        return
    }

    remaining.value -= 1

    if (remaining.value <= 0) {
        stop()
        window.location.assign(props.nextEpisode.url)
    }
}

onMounted(() => {
    if (countdownActive.value) {
        timer = setInterval(tick, 1000)
    }
})

onBeforeUnmount(stop)
</script>

<style>
.vp-end {
    position: absolute;
    z-index: 2;
    inset: 0;
    padding: 52px 56px 120px;
    overflow-y: auto;
    background: rgba(21, 18, 13, 0.88);
}

.vp-end-header {
    display: flex;
    align-items: center;
    gap: 12px;
}

.vp-end-body {
    margin-top: 36px;
    display: flex;
    align-items: stretch;
    gap: 32px;
}

.vp-end-main {
    min-width: 0;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.vp-end-next-label {
    color: var(--vp-muted);
}

.vp-end-next {
    padding: 16px;
    display: flex;
    gap: 18px;
    border: 1px solid rgba(251, 249, 245, 0.16);
    border-radius: 14px;
    color: var(--vp-paper);
    text-decoration: none;
    background: rgba(251, 249, 245, 0.07);
}

.vp-end-next-image {
    width: 208px;
    height: 117px;
    flex-shrink: 0;
    border-radius: 8px;
    object-fit: cover;
}

.vp-end-next-text {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.vp-end-next-title {
    font-size: 20px;
    line-height: 1.2;
}

.vp-end-next-duration {
    font-size: 12px;
    color: var(--vp-muted);
}

.vp-end-countdown {
    display: flex;
    align-items: center;
    gap: 12px;
}

.vp-end-countdown-track {
    position: relative;
    max-width: 240px;
    height: 4px;
    flex-grow: 1;
    border-radius: 2px;
    background: rgba(207, 200, 188, 0.26);
}

.vp-end-countdown-fill {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    border-radius: 2px;
    background: var(--vp-accent);
    transition: width 1s linear;
}

.vp-end-countdown-text {
    font-size: 12px;
    color: var(--vp-muted);
}

.vp-end-links {
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 10px;
}

.vp-end-link {
    font-size: 14px;
    font-weight: 600;
    color: var(--vp-accent);
    text-decoration: none;
    background: transparent;
}

.vp-end-follow {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.vp-promo {
    width: 404px;
    flex-shrink: 0;
    padding: 28px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-radius: 14px;
    color: var(--vp-ink);
    background: var(--vp-paper);
    box-shadow: 0 12px 32px rgba(21, 18, 13, 0.28);
}

.vp-promo-brand {
    display: flex;
    align-items: center;
    gap: 10px;
}

.vp-promo .vp-promo-label {
    color: #0b6f5e;
}

.vp-promo .vp-promo-title {
    margin: 0;
    font-size: 26px;
    line-height: 1.16;
    letter-spacing: -0.02em;
    color: var(--vp-ink);
}

.vp-promo-body {
    margin: 0;
    font-size: 15px;
    line-height: 1.55;
    color: #2f2a22;
}

.vp-promo-button {
    height: 48px;
    padding: 0 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 600;
    color: var(--vp-cta-text);
    text-decoration: none;
    background: var(--vp-cta);
}

.vp-promo-trust {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #6b6457;
}

.vp-promo-shield {
    color: #0b6f5e;
}

@media (max-width: 767px) {
    .vp-end {
        padding: 20px 16px 110px;
    }

    .vp-promo {
        display: none;
    }

    .vp-end-next-image {
        width: 120px;
        height: 68px;
    }
}
</style>
